import { useApp, useDeposit } from 'providers';
import React, { useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { ROUTES } from 'routes';
import { usePageTracking } from 'hooks';
import { TRACKING_PAGES } from 'api';
import { Loader, VehicleVersionForm } from 'components';
import { MatchImmatResponse, MatchImmatService } from 'api/Dab/MatchImmatService';
import { VehicleVersionChoiceType } from 'types';
import { useMountEffect } from 'hooks/use-mount-effect';
import './Deposit.scss';
import { ClassifiedManagementService } from 'api/Classifieds/ClassifiedManagementService';
import { DepositPageContainer } from './DepositPageContainer';

export const VehicleVersionChoicePage: React.FC = () => {
  const history = useHistory();
  const { user } = useApp();
  const [loading, setLoading] = useState(true);
  const { depositType, vehicle } = useDeposit();
  const [matchImmatResponse, setMatchImmatResponse] = useState<MatchImmatResponse | undefined>(undefined);

  if (!(vehicle.registration || vehicle.vin)) history.replace(ROUTES.registration);
  else if (!depositType) history.replace(ROUTES.vehicleType);

  usePageTracking(user!.isPro ? TRACKING_PAGES.VERSION_PRO : TRACKING_PAGES.VERSION_PART);

  const match = useCallback(async () => {
    if (vehicle.registration !== undefined || (!vehicle.registration && vehicle.vin)) {
      const response = await MatchImmatService.match(depositType!, vehicle.registration ?? '');
      await ClassifiedManagementService.updateClassified({ vehicle: { mraChecked: response.mra } }).catch(() => {});
      if (vehicle.version) {
        // extract fields if version is defined
        const {
          firstCirculationDate,
          make,
          model,
          version,
          imported,
          firstHand,
          mileage,
          condition,
          externalColor,
          paintType,
          internalColor,
          seatMaterial,
        } = vehicle;
        setMatchImmatResponse({
          ...response,
          vehicleVersion: {
            firstCirculationDate,
            make,
            model,
            version,
            imported,
            firstHand,
            mileage,
            condition,
            externalColor,
            paintType,
            internalColor,
            seatMaterial,
          },
        });
      } else {
        setMatchImmatResponse(response);
      }
    }
  }, [vehicle]);

  useMountEffect(() => {
    match().then(() => setLoading(false));
  });

  return (
    <DepositPageContainer isPro={user!.isPro} big>
      {loading ? (
        <Loader />
      ) : (
        matchImmatResponse && (
          <VehicleVersionForm
            defaultVehicle={matchImmatResponse.vehicleVersion}
            vehicleType={depositType!}
            isMatched={matchImmatResponse.vehicleVersionChoiceType === VehicleVersionChoiceType.MATCH}
            defaultVehicleMatchList={matchImmatResponse.vehicleMatchList}
          />
        )
      )}
    </DepositPageContainer>
  );
};
